export const USER_GUIDE_SECTIONS = [
  {
    id: "overview",
    title: "How to use this guide",
    path: null,
    steps: [
      "Open any topic from the chips above, or type in the search box to filter.",
      "Yellow Open this Admin page jumps to the matching screen so you can configure while you read.",
      "Sidebar → General → User Guide is always available. Help also links here.",
      "After you Save on Program & Subscriptions, Email Templates, Offer Modal, or Zoho Lab Form, Vitality uses the new values on the next load.",
      "Existing customers keep programs and memberships they already paid for. Hiding a plan from the storefront does not cancel their access.",
    ],
  },
  {
    id: "menu-map",
    title: "Admin menu map",
    path: "/dashboard",
    steps: [
      "Overview → Dashboard: content counts and under-review shortcut.",
      "Overview → Contents: approve, review, or send content back for rework.",
      "Overview → User Management: Users, Admins, and Teachers.",
      "Overview → Determinants: assessment library used on Vitality results.",
      "Overview → Program & Subscriptions: memberships, courses, and free-for-everyone courses.",
      "Overview → Portal Settings: member discount, lab panel pricing, Free Plan menu, and the Before You Begin assessment pop-up.",
      "Overview → Offer Modal: first-login promo on Vitality.",
      "Overview → Email Templates: OTP, lab invite, payment failed, and other outbound mail.",
      "Overview → Zoho Lab Form: Order Labs embed and which memberships see it.",
      "Overview → Financial Reports and Coupon Usage Report: payments and coupon redemptions.",
      "General → Settings: your profile, content threshold, auto-delete duration, notifications.",
      "General → Create Content: publish new assessment content as Admin.",
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard",
    path: "/dashboard",
    steps: [
      "Open Overview → Dashboard after login.",
      "Week / Month / Year switches the content chart range.",
      "Use the under-review count to jump to Contents filtered to Under Review.",
      "Dashboard is read-only analytics. Product configuration is on Program & Subscriptions and the other Overview items.",
    ],
  },
  {
    id: "programs-list",
    title: "Program & Subscriptions — list page",
    path: "/programs",
    steps: [
      "Go to Overview → Program & Subscriptions.",
      "Member discount, lab prices, Free Plan menu options, and the Before You Begin pop-up are on Overview → Portal Settings.",
      "Use the status switch on a row to activate or deactivate a product. Deactivated items stop showing to new buyers. People who already purchased them still keep access.",
      "Use Edit (pencil) to change a program or subscription. Use View (eye) to preview the listing.",
      "To open one course to non-members, edit it and tick Available to Free Plan on a $0 Program. See Free course for everyone (Available to Free Plan).",
      "Click + Add New in the top-right header to create a Program or Subscription.",
    ],
  },
  {
    id: "portal-settings",
    title: "Portal Settings",
    path: "/portal-settings",
    steps: [
      "Go to Overview → Portal Settings.",
      "Free Plan portal features: tick the menu options for users with no active subscription. Shop, Community, and Events start off. Free Plan users still get a Join pop-up for Community, Shop, and Events even if those boxes are on.",
      "Lab panel pricing: set Retail $, Member $, and % off, then Save. These numbers appear on the Order Labs pop-up.",
      "Before You Begin pop-up: turn it on or off, and edit the title, bullet points, signature, button label, and photo. Save applies it to the next new assessment. Leave the photo blank to keep the built-in founders image.",
      "Member discount on programs: enter a percent (0–100) and Save. Members are charged that price at checkout. They do not type a coupon for this discount.",
    ],
  },
  {
    id: "add-subscription",
    title: "Add a membership (Subscription)",
    path: "/programs/new",
    steps: [
      "On Program & Subscriptions, click + Add New in the header.",
      "Set Type to Subscription. Type cannot be changed after Create.",
      "Fill Name (for example Voyager), Amount, Currency, Unit / duration, image, and description.",
      "Category: select the LearnDash / WordPress category that holds courses included with this membership. For Voyager, only Basics of Biophysics should be in that category.",
      "Turn Show on Vitality portal on if new customers should see it on Memberships. Turn it off to stop selling it. Existing members on that plan keep access until expiry.",
      "Portal Menu Options: tick which Vitality menu items this membership includes (Order Labs, Book a Consult, Courses, Shop, Community, Events, Search, Last Session, Memberships). On a new record these save with Create. On an existing plan, click Save Menu Options — changes apply immediately for members on that plan.",
      "Leave Corporate Program off unless this is a corporate-only product (corporate items do not sell on the public Courses grid).",
      "Click Create. Attach a Stripe price for this subscription in the usual way so checkout works.",
    ],
  },
  {
    id: "add-paid-program",
    title: "Add a paid course (Program)",
    path: "/programs/new",
    steps: [
      "Click + Add New. Set Type to Program (not Subscription).",
      "Name should match the course name as you want it on Vitality Courses.",
      "Amount: the non-member price (greater than 0). Member price is calculated from the Member discount percent on the list page.",
      "LMS Url: paste the LearnDash course URL so Open works after purchase.",
      "Upload an image. Category is required. Corporate Program must be off for a normal sellable course.",
      "Turn Show on Vitality portal on and keep Status Active after save.",
      "Click Create and connect Stripe for this program.",
      "Result on Vitality: Free Plan and members see Unlock. After payment the button becomes Open. Non-members do not need a membership to buy it.",
      "Available to Free Plan stays greyed out here. It only works on a $0 course, so a paid program can never be given away by mistake.",
    ],
  },
  {
    id: "add-free-program",
    title: "Add a free / included course (Program)",
    path: "/programs/new",
    steps: [
      "Same as a paid Program, but set Amount to 0.",
      "Paste the LMS Url. Show on Vitality on. Corporate off.",
      "Amount 0 means included with membership on Courses: Free Plan sees it locked with Join; members see Open. There is no Stripe charge.",
      "Leave Available to Free Plan unticked for members-only. Tick it to make the course free for everyone — see Free course for everyone (Available to Free Plan).",
      "Do not also leave the same course in the Voyager WordPress category if you add it here, or it may appear twice. For Basics of Biophysics, either keep it only in the Voyager LearnDash category, or only as this $0 Admin program — not both.",
    ],
  },
  {
    id: "free-for-non-members",
    title: "Free course for everyone (Available to Free Plan)",
    path: "/programs",
    steps: [
      "Use this when a course should be open to people who have not bought a membership.",
      "Go to Overview → Program & Subscriptions, then + Add New for a new course or Edit (pencil) on an existing one.",
      "Type must be Program (not Subscription). Corporate Program must be off.",
      "Set Amount to 0. The Available to Free Plan tick box only becomes active when the amount is 0, so a paid course can never be given away by mistake.",
      "Tick Available to Free Plan. Paste the LMS Url so Open has somewhere to go, keep Show on Vitality portal on, and keep Status Active.",
      "Save. On Vitality Courses that card now shows a yellow Free badge, the words Free for everyone, and an Open button. Non-members go straight into the course with no payment and no Become a Member prompt.",
      "Free Plan users also need Courses ticked under Free Plan portal features on the list page, otherwise they cannot reach the Courses page at all.",
      "Untick Available to Free Plan and Save to make it members-only again. Non-members then see Included with membership and Become a Member as before.",
      "If you raise the Amount above 0 later, the free access is dropped automatically and the course becomes a normal paid program.",
      "This setting is per course. It does not change anyone's membership, and it does not give access to any other course.",
    ],
  },
  {
    id: "non-member-access",
    title: "Give a non-member access to a course",
    path: "/programs",
    steps: [
      "Free for everyone: set Amount 0 and tick Available to Free Plan. Anyone can open it, member or not.",
      "Sell it to them: create it as a paid Program with Amount greater than 0. They press Unlock and pay by card. No membership needed.",
      "Members only: set Amount 0 and leave Available to Free Plan unticked. Non-members see Become a Member.",
      "Admin cannot hand a members-only course to one single Free Plan user. Access is set per course, not per person, so use one of the first two options instead.",
      "Anyone who already paid for a course keeps it. Changing these settings never removes access someone has bought.",
    ],
  },
  {
    id: "lms-vitality",
    title: "LearnDash / LMS settings that affect Courses",
    path: "/programs",
    steps: [
      "Unchecking Program inside LearnDash does not hide a course on Vitality. Vitality does not read that checkbox.",
      "Included with membership: keep only those courses (Basics of Biophysics) in the WordPress category attached to the Voyager subscription.",
      "Remove Health Reset and other sellable courses from that Voyager category. They can stay published in LearnDash.",
      "Then create each sellable course as an Admin Program with Stripe (see Add a paid course).",
      "Existing customers who already bought a program still see Open on that course even if you hide it from the storefront.",
    ],
  },
  {
    id: "offer-modal",
    title: "Offer Modal (first-login promo)",
    path: "/offer-modal",
    steps: [
      "Go to Overview → Offer Modal.",
      "Enable the modal if it should show after first login on Vitality.",
      "Set title, body, button label, coupon code, and which subscription the checkout should use.",
      "Save. The coupon must also exist as an active Stripe promotion code with the same name.",
    ],
  },
  {
    id: "email-templates",
    title: "Email Templates",
    path: "/email-templates",
    steps: [
      "Go to Overview → Email Templates.",
      "Pick a template from the dropdown (OTP Verification, lab invite, payment failed, and others).",
      "Edit subject and HTML. Insert merge tags listed on the page (for example {{otp}}). Global tags work in every template.",
      "You can send a test email from this page before going live.",
      "Save. If a template was never saved, Vitality uses the built-in default.",
      "OTP email image: the default OTP template uses the results showcase image on japa-health-media. If you already saved the OTP template, replace the old image URL in the HTML and Save, or the old picture will keep sending.",
      "Purchase receipts: open Email Templates then the purchase-receipts link, or go to /email-templates/purchase-receipts, for guest / purchase emails.",
    ],
  },
  {
    id: "zoho-labs",
    title: "Zoho Lab Form / Order Labs",
    path: "/zoho-lab-form",
    steps: [
      "Go to Overview → Zoho Lab Form.",
      "Create or edit a config: paste the Zoho form script, choose which memberships it applies to, and enable it.",
      "Members on a matching plan see Order Labs, then the deal pop-up, then email / Zoho as configured.",
      "Free Plan menu label is Order Labs. They get the join / member-price pop-up, not the Zoho form.",
      "Lab retail, member price, and percent on Portal Settings drive the pop-up numbers.",
    ],
  },
  {
    id: "users",
    title: "User Management",
    path: "/user_management_list",
    steps: [
      "Go to Overview → User Management.",
      "Switch Users / Admins / Teachers in the tabs at the top.",
      "Open a user to see profile, assessments, score, and payment history.",
      "You can activate or deactivate accounts from user details. That does not delete their past purchases.",
      "Create User in the header adds an Admin or Teacher account (not a Vitality customer).",
    ],
  },
  {
    id: "contents",
    title: "Contents (review library)",
    path: "/content/all",
    steps: [
      "Go to Overview → Contents.",
      "Filter with All, Approved, Under Review, Rework, or Disapproved.",
      "Switch Grid / List in the header.",
      "Open a card to approve, request rework, or disapprove. Approved items can appear in the Vitality assessment library.",
      "This is the content workflow. Courses on the Vitality Courses page are configured under Program & Subscriptions, not here.",
    ],
  },
  {
    id: "create-content",
    title: "Create Content",
    path: "/content/create_new_content",
    steps: [
      "Go to General → Create Content.",
      "Step through Choose Teacher, Choose Determinants, Choose Content Type, Content Description, then Preview.",
      "Upload video or other files as prompted, then submit.",
      "New items typically land in Under Review on Contents until an Admin approves them.",
    ],
  },
  {
    id: "determinants",
    title: "Determinants",
    path: "/determinants",
    steps: [
      "Go to Overview → Determinants.",
      "This is the assessment library (categories, practices, suggestions) used on Vitality results.",
      "Click a row to open categories and practices. Lesson titles and Proceed to goal links come from this data plus the LMS URL on each suggestion.",
      "DEV and Production have separate databases. Old titles on DEV usually mean DEV data, not a broken button.",
    ],
  },
  {
    id: "reports",
    title: "Financial Reports",
    path: "/financial-reports",
    steps: [
      "Go to Overview → Financial Reports.",
      "Filter by today, week, month, quarter, year, multi-year, lifetime, or a custom date range.",
      "Export when you need a spreadsheet of the current view.",
      "Member course discount is not a coupon the member types. It is the percent on Portal Settings.",
    ],
  },
  {
    id: "coupon-usage",
    title: "Coupon Usage Report",
    path: "/coupon-usage-report",
    steps: [
      "Go to Overview → Coupon Usage Report.",
      "Filter by coupon code and date range, then export CSV if needed.",
      "This shows redemption history. To change which promo appears at first login, use Offer Modal. To change member course pricing, use Member discount on Portal Settings.",
    ],
  },
  {
    id: "settings",
    title: "Settings",
    path: "/Setting",
    steps: [
      "Go to General → Settings.",
      "Edit updates your Admin profile name and related fields.",
      "Approved content Threshold: how many approved items go into each subcategory.",
      "Auto Delete duration: how long disapproved content stays before it is removed.",
      "Content Notification and User Notification switches control Admin alerts for content and invites.",
    ],
  },
  {
    id: "existing-customers",
    title: "Existing customers (already purchased)",
    path: "/user_management_list",
    steps: [
      "People with an active subscription stay members until the expiry date, including cancel-at-period-end.",
      "Each program they already bought stays Open on Courses, even if you turn Show on Vitality off.",
      "Explorer or Transformer members keep that plan until it ends. Hiding it from Memberships only stops new sales.",
      "If you turn off a Portal Menu Option on their plan, that menu item can hide. That is the only Admin action that changes their menu without touching payment.",
      "Unticking Available to Free Plan on a $0 course only affects non-members. Members and anyone who purchased a course are not affected.",
    ],
  },
];
